import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import type { ApplicationDocumentRow, DocumentRequirement } from "../types";

const SIZE = 56;
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * « Portail/Documents — bouton » (Figma `425:15125`, US-E3-08 §3.3) — the persistent trigger that
 * opens the documents sheet, living in the bottom action bar alongside Next/Submit.
 *
 * ⚠️ Figma's own component description flags this as library-absent — "anneau de progression +
 * pastille sur icône" (a progress ring + status badge on an icon) exists in no primitive
 * already in this codebase, so it's built here from raw SVG and
 * tokens rather than approximated with a borrowed component. Ring = share of REQUIRED documents
 * `AVAILABLE`, on the same menthe token `FoundationStepper`'s own completion track uses — never
 * regresses, matching that design rule verbatim. Badge = a distinct signal, priority strict order
 * Figma specifies: **attention > done > none** — a `REJECTED` required document always wins even
 * if the ring is otherwise full (a full ring with a rejected file is not actually complete).
 *
 * The label is the exact pattern Figma writes on `État=Vide`: state AND count together, never
 * "Documents" alone — a badge or ring alone says nothing to a screen reader.
 */
export function DocumentsButton({
  requirements,
  documents,
  onPress,
}: {
  requirements: DocumentRequirement[];
  documents: ApplicationDocumentRow[];
  onPress: () => void;
}) {
  const { t } = useTranslation("acquire");

  const requiredRequirements = requirements.filter((req) => req.is_required);
  const sentCount = requiredRequirements.filter((req) =>
    documents.some(
      (doc) => doc.requirement_id === req.id && doc.status === "AVAILABLE"
    )
  ).length;
  const requiredTotal = requiredRequirements.length;
  const hasRejected = requiredRequirements.some((req) =>
    documents.some(
      (doc) => doc.requirement_id === req.id && doc.status === "REJECTED"
    )
  );
  const allSent = requiredTotal > 0 && sentCount === requiredTotal;

  const progress = requiredTotal === 0 ? 0 : sentCount / requiredTotal;
  const dashoffset = CIRCUMFERENCE * (1 - progress);

  const label =
    requiredTotal === 0
      ? t("dossier.documents.button-label-none")
      : t("dossier.documents.button-label", {
          sent: sentCount,
          total: requiredTotal,
        });

  return (
    <button
      type="button"
      onClick={onPress}
      aria-label={label}
      className="relative shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2"
      style={{ width: SIZE, height: SIZE }}
    >
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        aria-hidden
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="var(--surface-card)"
          stroke="var(--border-default)"
          strokeWidth={STROKE}
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="var(--color-menthe-600)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashoffset}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        />
      </svg>
      <span
        aria-hidden
        className="absolute inset-0 flex items-center justify-center text-(--text-secondary)"
      >
        <Icon name="file-text" size={20} />
      </span>
      {hasRejected || allSent ? (
        <span
          aria-hidden
          className={
            "absolute -right-0.5 -top-0.5 size-3.5 rounded-full ring-2 ring-(--surface-card) " +
            (hasRejected
              ? "bg-(--feedback-warning-icon)"
              : "bg-(--feedback-success-icon)")
          }
        />
      ) : null}
    </button>
  );
}
